import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Trash2, Eraser } from "lucide-react";
import EndpointCard from "@/components/EndpointCard";
import WebhookCard from "@/components/WebhookCard";
import ProviderConfig from "@/components/ProviderConfig";
import TransactionsTable from "@/components/TransactionsTable";
import CheckoutTester from "@/components/CheckoutTester";
import InventoryManager from "@/components/InventoryManager";
import ShippoSettings from "@/components/ShippoSettings";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function SubAccountDetail({ sub, onChange, onDelete, isAdmin = true, baseUrl }) {
  const [txns, setTxns] = useState([]);
  const [clearing, setClearing] = useState(false);

  const loadTxns = useCallback(async () => {
    try {
      const env = sub.square_env || "sandbox";
      const { data } = await api.get(`/subaccounts/${sub.id}/transactions?environment=${env}`);
      setTxns(data);
    } catch {
      /* ignore */
    }
  }, [sub.id, sub.square_env]);

  const clearTestOrders = async () => {
    if (!window.confirm(`Permanently delete ALL test/sandbox orders for "${sub.name}"?\n\nLive (production) orders are never affected. This cannot be undone.`)) return;
    setClearing(true);
    try {
      const { data } = await api.delete(`/orders?environment=sandbox&subaccount_id=${sub.id}`);
      toast.success(`Cleared ${data.deleted} test order${data.deleted === 1 ? "" : "s"}`);
      await loadTxns();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setClearing(false);
    }
  };

  useEffect(() => {
    loadTxns();
  }, [loadTxns]);

  const remove = async () => {
    if (!window.confirm(`Delete "${sub.name}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/subaccounts/${sub.id}`);
      toast.success("Sub-account deleted");
      onDelete(sub.id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="space-y-4" data-testid="subaccount-detail">
      <div className="flex items-start justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">{sub.name}</h2>
          <p className="font-mono text-xs text-muted-foreground mt-0.5">GHL Sub-Account · {sub.ghl_subaccount_id}</p>
        </div>
        {isAdmin && (
          <Button variant="ghost" size="sm" data-testid="delete-subaccount-btn" onClick={remove} className="text-destructive hover:text-destructive hover:bg-destructive/10">
            <Trash2 className="h-4 w-4" />
          </Button>
        )}
      </div>

      <Tabs defaultValue="endpoint">
        <TabsList className="bg-secondary">
          <TabsTrigger value="endpoint" data-testid="tab-endpoint">Endpoint</TabsTrigger>
          <TabsTrigger value="providers" data-testid="tab-providers">Providers</TabsTrigger>
          <TabsTrigger value="inventory" data-testid="tab-inventory">Inventory</TabsTrigger>
          <TabsTrigger value="shipping" data-testid="tab-shipping">Shipping</TabsTrigger>
          <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
          <TabsTrigger value="test" data-testid="tab-test">Test</TabsTrigger>
        </TabsList>

        <TabsContent value="endpoint" className="mt-4 space-y-4">
          <EndpointCard sub={sub} onChange={onChange} baseUrl={baseUrl} />
          <WebhookCard sub={sub} onChange={onChange} />
        </TabsContent>
        <TabsContent value="providers" className="mt-4">
          <ProviderConfig sub={sub} onChange={onChange} />
        </TabsContent>
        <TabsContent value="shipping" className="mt-4">
          <ShippoSettings sub={sub} onChange={onChange} />
        </TabsContent>
        <TabsContent value="inventory" className="mt-4">
          <InventoryManager sub={sub} isAdmin={isAdmin} />
        </TabsContent>
        <TabsContent value="transactions" className="mt-4 space-y-3">
          <div className="flex items-center justify-between">
            <p className="font-mono text-xs text-muted-foreground">
              Showing <span className="text-foreground">{sub.square_env || "sandbox"}</span> orders
            </p>
            <Button
              variant="outline"
              size="sm"
              data-testid="clear-test-orders-btn"
              disabled={clearing}
              onClick={clearTestOrders}
              className="h-8 gap-1.5 text-[11px] text-destructive border-destructive/40 hover:bg-destructive/10 hover:text-destructive"
            >
              <Eraser className="h-3.5 w-3.5" />
              {clearing ? "Clearing…" : "Clear test orders"}
            </Button>
          </div>
          <TransactionsTable txns={txns} onRefresh={loadTxns} />
        </TabsContent>
        <TabsContent value="test" className="mt-4">
          <CheckoutTester sub={sub} onDone={loadTxns} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
